import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import * as auth from '../api/auth';
import { User } from '../api/auth';
import { isLoggedIn as hasStoredAuthHeaders } from '../api/client';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, passwordConfirm: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface OwnProps {
  children?: ReactNode;
}

const AuthProvider = (props: OwnProps) => {
  const { children } = props;
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const restoreSession = async () => {
      if (hasStoredAuthHeaders()) {
        const validatedUser = await auth.validateToken();
        setUser(validatedUser);
      }
      setLoading(false);
    };

    restoreSession();
  }, []);

  const login = async (email: string, password: string) => {
    const loggedInUser = await auth.login(email, password);
    setUser(loggedInUser);
  };

  const register = async (email: string, password: string, passwordConfirm: string) => {
    await auth.register(email, password, passwordConfirm);
    await login(email, password);
  };

  const logout = async () => {
    await auth.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export {
  AuthProvider,
  useAuth,
};
